import React, { useState } from "react";

/**
 * QuickActionsFAB — bottom-right floating button. Tap to fan out a radial
 * menu of the 6 most-used actions. Lives above everything (z-index 14) but
 * below the universal command bar so they don't fight.
 *
 * Particularly useful on mobile / PWA where the CommandRail is hidden.
 */
const ACTIONS = [
  { id: "capital_pnl",    label: "P&L",      phrase: "show me my live capital P&L",       icon: "📈", hint: "capital_pnl" },
  { id: "morning_brief",  label: "Brief",    phrase: "give me my morning brief",          icon: "☀", hint: "morning_brief" },
  { id: "raw_hub",        label: "Hub",      phrase: "open my RAW Company hub dashboard", icon: "🌐", hint: "raw_hub_dashboard" },
  { id: "agents",         label: "Agents",   phrase: "open agent studio",                 icon: "🤖", hint: "agent_studio" },
  { id: "journey",        label: "Journey",  phrase: "tell me my emergent journey and give me the proud rundown", icon: "🏆", hint: "my_journey" },
  { id: "system_map",     label: "Map",      phrase: "show me the system map",             icon: "🛰", hint: "show_architecture" },
  { id: "overnight",      label: "Overnight",phrase: "what did you do overnight",         icon: "🌙" },
  { id: "settings",       label: "Settings", phrase: "open settings",                      icon: "⚙" },
  { id: "commands",       label: "Commands", phrase: "show me the cheat sheet",            icon: "📋" },
];

export default function QuickActionsFAB({ onPhrase }) {
  const [open, setOpen] = useState(false);

  const fire = (action) => {
    setOpen(false);
    onPhrase?.(action.phrase, action.hint);
  };

  return (
    <div className="quick-fab-root" data-testid="quick-fab-root">
      {open && (
        <div className="quick-fab-fan" data-testid="quick-fab-fan">
          {ACTIONS.map((a, i) => {
            // Fan-out spread. With ≤6 actions a 90° quarter-circle is fine
            // (original design), but ≥7 actions need a full 180° half-circle
            // at a larger radius — otherwise adjacent buttons overlap so
            // badly that the upper item swallows clicks meant for the lower
            // (real iter12 hit-target bug). Recalculate based on length.
            const wide = ACTIONS.length > 6;
            const arc = wide ? 180 : 90;
            // start=-270° makes the wide half-circle bow STRAIGHT LEFT from the
            // right-anchored FAB. All cos() values from -270° to -90° are ≤ 0
            // so every chip's x-offset is ≤ 0 — nothing spills off the right
            // viewport edge. (iter13 found that -210° still put the last chip
            // 138px right of the anchor → off-screen on a 1920px viewport.)
            const start = wide ? -270 : -180;
            const r = wide ? 160 : 110;
            const angle = start + (i * (arc / (ACTIONS.length - 1)));
            const rad = (angle * Math.PI) / 180;
            const x = Math.cos(rad) * r;
            const y = Math.sin(rad) * r;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => fire(a)}
                className="quick-fab-item"
                style={{ transform: `translate(${x}px, ${y}px)` }}
                data-testid={`quick-fab-${a.id}`}
                title={a.label}
              >
                <span className="quick-fab-item-icon">{a.icon}</span>
                <span className="quick-fab-item-label">{a.label}</span>
              </button>
            );
          })}
        </div>
      )}
      <button
        type="button"
        className={`quick-fab-main ${open ? "is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        data-testid="quick-fab-toggle"
        aria-label={open ? "Close quick actions" : "Open quick actions"}
      >
        {open ? "✕" : "⚡"}
      </button>
    </div>
  );
}
